import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import ProductFeatureVisiblity from '@/models/Products/ProductFeatureVisiblity';
import ListUtils from '@/lib/listUtils';
import CurrencyUtils from '@/lib/currencyUtils';
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import RateSource from '@/models/Products/RateSources/RateSource';
import UpdateAccountDepositBankSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositBankSettingsRequest';
import UpdateAccountDepositCardSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositCardSettingsRequest';
import UpdateAccountDepositPaymentOrderSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositPaymentOrderSettingsRequest';
import UpdateAccountDepositInterestSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositInterestSettingsRequest';
import UpdateAccountDepositInstantAccessSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositInstantAccessSettingsRequest';
import UpdateAccountDepositOverdraftInterestSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositOverdraftInterestSettingsRequest';
import UpdateAccountDepositOverdraftSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositOverdraftSettingsRequest';
import DepositAccountSettings from '@/models/Accounts/DepositAccounts/Settings/DepositAccountSettings';
import UpdateAccountGeneralProductSettingsRequest from '@/models/Accounts/Shared/UpdateAccountGeneralProductSettingsRequest';
import UpdateGeneralPayeeSettingsRequest from '@/models/Products/ProductSettings/DefaultSettings/GeneralPayee/UpdateGeneralPayeeSettingsRequest';
import UpdateDepositFixedTermSettingsRequest from '@/models/Products/ProductSettings/DefaultSettings/FixedTerm/UpdateDepositFixedTermSettingsRequest';
import UpdateDepositFixedTermMaturitySettingsRequest from '@/models/Products/ProductSettings/DefaultSettings/FixedTermMaturity/UpdateDepositFixedTermMaturitySettingsRequest';
import UpdateAccountDepositCoolingOffSettingsRequest from '@/models/Accounts/DepositAccounts/Settings/UpdateAccountDepositCoolingOffSettingsRequest';

export interface IDepositAccountSettingsState {
    loading: boolean;
    loadingDialog: boolean;
    accountId: string;
    settings: DepositAccountSettings;
    productId: string;
    productIssueId: string;
    productIssue: ProductIssue;
    productFeatureVisibility: ProductFeatureVisiblity;
    rateSources: RateSource[];
    fixedTermMaturityAction1ProductIssues: [];
    fixedTermMaturityAction2ProductIssues: [];
    fixedTermMaturityAction3ProductIssues: [];
    fixedTermMaturityAction4ProductIssues: [];
}

@Module({ dynamic: true, store, name: 'DepositAccountSettingsModule', namespaced: true })
class DepositAccountSettingsStore extends VuexModule implements IDepositAccountSettingsState {

    public loading: boolean = true;
    public loadingDialog: boolean = false;
    public accountId: string = '';
    public settings: DepositAccountSettings = new DepositAccountSettings();
    public productId: string = '';
    public productIssueId: string = '';
    public productIssue: ProductIssue = new ProductIssue();
    public productFeatureVisibility = new ProductFeatureVisiblity();
    public rateSources: RateSource[] = [];
    public fixedTermMaturityAction1ProductIssues: [] = [];
    public fixedTermMaturityAction2ProductIssues: [] = [];
    public fixedTermMaturityAction3ProductIssues: [] = [];
    public fixedTermMaturityAction4ProductIssues: [] = [];

    @Action
    async getAccount(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${id}`)
        .then(response => {
            this.context.commit("setAccountId", id);
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.context.commit("setProductId", response.data.productId);
            this.context.commit("setProductIssueId", response.data.productIssueId);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getProductIssue() {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/product-issues/${this.productIssueId}`)
        .then(response => {
            this.context.commit("setProductIssue", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async editGeneralProductSettings(request: UpdateAccountGeneralProductSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/general-product-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositBankSettings(request: UpdateAccountDepositBankSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/accounts/${this.accountId}/deposit-bank-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositCardSettings(request: UpdateAccountDepositCardSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/accounts/${this.accountId}/deposit-card-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editGeneralPayeeSettings(request: UpdateGeneralPayeeSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/general-payee-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteGeneralPayeeSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/general-payee-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositPaymentOrderSettings(request: UpdateAccountDepositPaymentOrderSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/accounts/${this.accountId}/deposit-payment-order-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositPaymentOrderSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/accounts/${this.accountId}/deposit-payment-order-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositInterestSettings(request: UpdateAccountDepositInterestSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-interest-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositInterestSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-interest-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositInstantAccessSettings(request: UpdateAccountDepositInstantAccessSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-instant-access-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositInstantAccessSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-instant-access-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositOverdraftSettings(request: UpdateAccountDepositOverdraftSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-overdraft-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositOverdraftSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-overdraft-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositOverdraftInterestSettings(request: UpdateAccountDepositOverdraftInterestSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-overdraft-interest-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositOverdraftInterestSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-overdraft-interest-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositFixedTermSettings(request: UpdateDepositFixedTermSettingsRequest) {

        this.setLoadingDialog(true);
        request.fundingWindowPeriodValue = +request.fundingWindowPeriodValue;
        request.minimumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.minimumBalance.amount);
        request.minimumBalance.currency = this.productIssue.generalSettings.defaultWalletCurrency;
        request.maximumBalance.amount = CurrencyUtils.convertDecimalStringToAmountString(request.maximumBalance.amount);
        request.maximumBalance.currency = this.productIssue.generalSettings.defaultWalletCurrency;
        request.termPeriodValue = +request.termPeriodValue;

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-fixed-term-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositFixedTermSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-fixed-term-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositFixedTermMaturitySettings(request: UpdateDepositFixedTermMaturitySettingsRequest) {

        this.setLoadingDialog(true);

        if (request.maturityAction1Type) {
            request.maturityAction1Amount.amount = CurrencyUtils.convertDecimalStringToAmountString(request.maturityAction1Amount.amount);
            request.maturityAction1Amount.currency = this.productIssue.generalSettings.defaultWalletCurrency;
        }

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-fixed-term-maturity-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositFixedTermMaturitySettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-fixed-term-maturity-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editDepositCoolingOffSettings(request: UpdateAccountDepositCoolingOffSettingsRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-cooling-off-settings`, request)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteDepositCoolingOffSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/accounts/${this.accountId}/deposit-cooling-off-settings`)
        .then(response => {
            this.context.commit("setAccountSettings", response.data.accountSettings);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getFixedTermMaturityAction1ProductIssues(productId: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productId}`)
        .then(response => {
            this.context.commit("setFixedTermMaturityAction1ProductIssues", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getFixedTermMaturityAction2ProductIssues(productId: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productId}`)
        .then(response => {
            this.context.commit("setFixedTermMaturityAction2ProductIssues", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getFixedTermMaturityAction3ProductIssues(productId: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productId}`)
        .then(response => {
            this.context.commit("setFixedTermMaturityAction3ProductIssues", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getFixedTermMaturityAction4ProductIssues(productId: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productId}`)
        .then(response => {
            this.context.commit("setFixedTermMaturityAction4ProductIssues", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getRateSources() {

        this.setLoadingDialog(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources/`)
        .then(response => {
            this.context.commit("setDepositAccountRateSources", response.data.results);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingDialog = false;
        this.productFeatureVisibility = new ProductFeatureVisiblity();
        this.accountId = ''
        this.settings = new DepositAccountSettings();
        this.productId = '';
        this.productIssueId = '';
        this.productIssue = new ProductIssue();
        this.rateSources = [];
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setProductIssue(productIssue: ProductIssue) {
        this.productIssue = productIssue;
        this.productFeatureVisibility = ListUtils.productFeatureVisibility(productIssue.generalSettings.subType);
    }

    @Mutation
    private async setProductId(id: string) {
        this.productId = id;
    }

    @Mutation
    private async setProductIssueId(id: string) {
        this.productIssueId = id;
    }

    @Mutation
    private async setAccountSettings(customerAccountSettings: DepositAccountSettings) {
        this.settings = customerAccountSettings;
    }

    @Mutation
    private async setAccountId(accountId: string) {
        this.accountId = accountId;
    }

    @Mutation
    private async setDepositAccountRateSources(rateSources: RateSource[]) {
        if (rateSources) {
            this.rateSources = rateSources;
        }
    }

    @Mutation
    private async setFixedTermMaturityAction1ProductIssues(response: []) {
        if (response) {
            this.fixedTermMaturityAction1ProductIssues = response;
        }
    }

    @Mutation
    private async setFixedTermMaturityAction2ProductIssues(response: []) {
        if (response) {
            this.fixedTermMaturityAction2ProductIssues = response;
        }
    }

    @Mutation
    private async setFixedTermMaturityAction3ProductIssues(response: []) {
        if (response) {
            this.fixedTermMaturityAction3ProductIssues = response;
        }
    }

    @Mutation
    private async setFixedTermMaturityAction4ProductIssues(response: []) {
        if (response) {
            this.fixedTermMaturityAction4ProductIssues = response;
        }
    }

}

export const DepositAccountSettingsModule = getModule(DepositAccountSettingsStore);